<template>
  <div class="ui-switch">
    <input
      type="checkbox"
      :id="id"
      :disabled="disabled"
      class="ui-switch__input"
      :checked="isChecked"
      @change="setValue"
    />
    <label :for="id" class="ui-switch__label label">
      <span class="ui-switch__label__text" v-if="reverse">
        <slot></slot>
      </span>
      <span
        class="ui-switch__label__track"
        :class="{ 'ui-switch__label__track--reverse': reverse, 'ui-switch__label__track--standalone': standalone }"
      >
        <span class="ui-switch__label__track__circle"></span>
      </span>
      <span class="ui-switch__label__text" v-if="!reverse">
        <slot></slot>
      </span>
    </label>
  </div>
</template>

<script>
export default {
  name: 'UiSwitch',
  model: {
    prop: 'value',
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    value: [Array, Boolean, Number, String],
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    reverse: {
      type: Boolean,
      required: false,
      default: false,
    },
    standalone: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    isChecked() {
      return !!this.value
    },
  },
  methods: {
    setValue(e) {
      const checked = e.target.checked
      /**
       * Emitted when the switch is clicked.
       * @event input
       * @type {Event}
       */
      this.$emit('input', checked)
    },
  },
}
</script>

<style lang="scss" scoped>
.ui-switch {
  display: inline-flex;

  &__label {
    display: flex;
    position: relative;
    align-items: center;
    cursor: pointer;

    .ui-switch__input:disabled + & {
      cursor: default;
    }

    &__track {
      position: relative;
      transition: all $transition-duration-default $transition-effect-default;
      margin-right: 12px;
      border-radius: 50px;
      background-color: $switch-color-bg;
      width: $switch-width;
      height: $switch-height;

      &--reverse {
        margin-right: 0;
        margin-left: 12px;
      }

      &--standalone {
        margin: 0;
      }

      .ui-switch__input:checked + label > & {
        background-color: $switch-color-bg-active;
      }

      .ui-switch__input:disabled + label > & {
        background-color: $switch-color-bg-disabled;
      }

      .ui-switch__input:checked:disabled + label > & {
        background-color: rgba($switch-color-bg-active, 0.4);
      }

      &__circle {
        position: absolute;
        top: 2px;
        bottom: 2px;
        left: 2px;
        transition: transform $transition-duration-default $transition-effect-default;
        border-radius: 50%;
        box-shadow: $switch-box-shadow rgba($generic-color-text, 0.2);
        background-color: $switch-color-bg-circle;
        width: calc(#{$switch-height} - 4px);

        .ui-switch__input:checked + label & {
          transform: translateX(100%);
        }
      }
    }

    &__text {
      flex: 1;
      transition: color $transition-duration-default $transition-effect-default;
      color: $switch-color-text;

      .ui-switch__input:disabled + label > & {
        color: $switch-color-text-disabled;
      }

      .ui-switch__input:hover:not(:disabled) + label > &,
      .ui-switch__input:focus:not(:disabled) + label > & {
        color: $switch-color-text-active;
      }
    }
  }

  &__input {
    position: absolute;
    margin: -1px;
    border: 0;
    padding: 0;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
}
</style>
